import React, { useState, useMemo } from 'react';
import Header from '../../components/ui/Header';
import AdminSidebar from '../../components/ui/AdminSidebar';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import SourceMediaPanel from './components/SourceMediaPanel';
import TargetCoursePanel from './components/TargetCoursePanel';
import MappingConnectionsOverlay from './components/MappingConnectionsOverlay';
import BulkMappingTools from './components/BulkMappingTools';
import ConfigurationPanel from './components/ConfigurationPanel';
import PreviewModal from './components/PreviewModal';

// Mock data for media mapping configuration
const mockMediaLibrary = [
  {
    id: 1,
    type: 'video',
    title: 'Introduction to JavaScript',
    filename: 'intro-javascript.mp4',
    url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
    thumbnail: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=300&h=200',
    size: '45.2 MB',
    duration: '12:34',
    quality: '1080p',
    tags: ['javascript', 'intro', 'programming'],
    usageCount: 3,
    lastUsed: '2025-01-10',
    mapped: true
  },
  {
    id: 2,
    type: 'image',
    title: 'React Components Diagram',
    filename: 'react-components.png',
    url: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=600',
    thumbnail: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=300&h=200',
    size: '2.1 MB',
    dimensions: '1920x1080',
    tags: ['react', 'components', 'diagram'],
    usageCount: 1,
    lastUsed: '2025-01-08',
    mapped: false
  },
  {
    id: 3,
    type: 'video',
    title: 'Advanced CSS Techniques',
    filename: 'advanced-css.mp4',
    url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_2mb.mp4',
    thumbnail: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200',
    size: '89.5 MB',
    duration: '28:15',
    quality: '720p',
    tags: ['css', 'advanced', 'styling'],
    usageCount: 0,
    lastUsed: null,
    mapped: false
  },
  {
    id: 4,
    type: 'document',
    title: 'JavaScript Cheat Sheet',
    filename: 'js-cheatsheet.pdf',
    url: '/assets/documents/js-cheatsheet.pdf',
    thumbnail: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=200',
    size: '1.2 MB',
    pages: 4,
    tags: ['javascript', 'cheatsheet', 'reference'],
    usageCount: 5,
    lastUsed: '2025-01-12',
    mapped: true
  },
  {
    id: 5,
    type: 'audio',
    title: 'Programming Podcast Episode 1',
    filename: 'programming-podcast-ep1.mp3',
    url: '/assets/audio/programming-podcast-ep1.mp3',
    thumbnail: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=300&h=200',
    size: '15.7 MB',
    duration: '32:45',
    tags: ['podcast', 'programming', 'discussion'],
    usageCount: 2,
    lastUsed: '2025-01-05',
    mapped: false
  }
];

const mockCourseStructure = [
  {
    id: 1,
    type: 'course',
    title: 'Full-Stack Web Development',
    modules: [
      {
        id: 101,
        title: 'Frontend Fundamentals',
        lessons: [
          { id: 1001, title: 'HTML Basics', mediaRequired: true, currentMedia: null, mediaType: 'video' },
          { id: 1002, title: 'CSS Styling', mediaRequired: true, currentMedia: { id: 1, type: 'video' }, mediaType: 'video' },
          { id: 1003, title: 'JavaScript Introduction', mediaRequired: true, currentMedia: null, mediaType: 'video' }
        ]
      },
      {
        id: 102,
        title: 'React Development',
        lessons: [
          { id: 1004, title: 'React Components', mediaRequired: true, currentMedia: { id: 2, type: 'image' }, mediaType: 'image' },
          { id: 1005, title: 'State Management', mediaRequired: false, currentMedia: null, mediaType: 'video' },
          { id: 1006, title: 'React Hooks', mediaRequired: true, currentMedia: null, mediaType: 'video' }
        ]
      }
    ]
  },
  {
    id: 2,
    type: 'course',
    title: 'Advanced JavaScript',
    modules: [
      {
        id: 201,
        title: 'ES6+ Features',
        lessons: [
          { id: 2001, title: 'Arrow Functions', mediaRequired: true, currentMedia: null, mediaType: 'video' },
          { id: 2002, title: 'Async/Await', mediaRequired: true, currentMedia: null, mediaType: 'video' }
        ]
      }
    ]
  }
];

const mockMappingConnections = [
  { sourceId: 1, targetId: 1002, connectionId: 'conn-1', status: 'active' },
  { sourceId: 2, targetId: 1004, connectionId: 'conn-2', status: 'active' },
  { sourceId: 4, targetId: 1003, connectionId: 'conn-3', status: 'conflict' }
];

const MediaMappingConfiguration = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [mappingStatusFilter, setMappingStatusFilter] = useState('all');
  const [selectedSourceItems, setSelectedSourceItems] = useState([]);
  const [selectedTargetItems, setSelectedTargetItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [connections, setConnections] = useState(mockMappingConnections);
  const [showBulkTools, setShowBulkTools] = useState(false);
  const [showConfiguration, setShowConfiguration] = useState(false);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, mapping: null });
  const [validationErrors, setValidationErrors] = useState([]);

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Admin', path: '/admin-panel-dashboard' },
    { label: 'Configurações', path: '/admin-panel-dashboard' },
    { label: 'Mapeamento de Mídia', path: '/media-mapping-configuration', isActive: true }
  ];

  // Filter media library based on search and filters
  const filteredMedia = useMemo(() => {
    return mockMediaLibrary?.filter(item => {
      const matchesSearch = item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           item?.filename?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           item?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
      
      const matchesType = mediaTypeFilter === 'all' || item?.type === mediaTypeFilter;
      
      const matchesStatus = mappingStatusFilter === 'all' || 
                           (mappingStatusFilter === 'mapped' && item?.mapped) ||
                           (mappingStatusFilter === 'unmapped' && !item?.mapped) ||
                           (mappingStatusFilter === 'conflict' && connections?.some(conn => 
                             conn?.sourceId === item?.id && conn?.status === 'conflict'));
      
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [searchTerm, mediaTypeFilter, mappingStatusFilter, connections]);

  // Calculate mapping statistics
  const mappingStats = useMemo(() => {
    const totalMedia = mockMediaLibrary?.length || 0;
    const mappedCount = mockMediaLibrary?.filter(item => item?.mapped)?.length || 0;
    const conflictCount = connections?.filter(conn => conn?.status === 'conflict')?.length || 0;
    const unmappedCount = totalMedia - mappedCount;

    return {
      total: totalMedia,
      mapped: mappedCount,
      unmapped: unmappedCount,
      conflicts: conflictCount,
      mappingRate: totalMedia > 0 ? Math.round((mappedCount / totalMedia) * 100) : 0
    };
  }, [connections]);

  const typeFilterOptions = [
    { value: 'all', label: 'Todos os Tipos' },
    { value: 'video', label: 'Vídeos' },
    { value: 'image', label: 'Imagens' },
    { value: 'document', label: 'Documentos' },
    { value: 'audio', label: 'Áudio' }
  ];

  const statusFilterOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'mapped', label: 'Mapeados' },
    { value: 'unmapped', label: 'Não Mapeados' },
    { value: 'conflict', label: 'Conflitos' }
  ];

  // Handle drag and drop operations
  const handleDragStart = (item) => {
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDropOnTarget = (targetItem) => {
    if (!draggedItem) return;

    // Create new connection
    const newConnection = {
      sourceId: draggedItem?.id,
      targetId: targetItem?.id,
      connectionId: `conn-${Date.now()}`,
      status: 'active'
    };

    // Check for conflicts
    const existingConnection = connections?.find(conn => conn?.targetId === targetItem?.id);
    if (existingConnection) {
      newConnection.status = 'conflict';
      setValidationErrors(prev => [...prev, `Conflito detectado: ${targetItem?.title} já possui mídia mapeada`]);
    }

    setConnections(prev => [...prev, newConnection]);
    
    // Update media mapped status
    const updatedMedia = mockMediaLibrary?.map(item => 
      item?.id === draggedItem?.id ? { ...item, mapped: true } : item
    );
    
    setDraggedItem(null);
    
    // Show success toast
    console.log(`Mídia "${draggedItem?.title}" mapeada para "${targetItem?.title}"`);
  };

  // Handle connection removal
  const handleRemoveConnection = (connectionId) => {
    const connection = connections?.find(conn => conn?.connectionId === connectionId);
    if (!connection) return;

    setConnections(prev => prev?.filter(conn => conn?.connectionId !== connectionId));
    
    // Update media mapped status if no other connections exist
    const hasOtherConnections = connections?.some(conn => 
      conn?.sourceId === connection?.sourceId && conn?.connectionId !== connectionId
    );
    
    if (!hasOtherConnections) {
      const updatedMedia = mockMediaLibrary?.map(item => 
        item?.id === connection?.sourceId ? { ...item, mapped: false } : item
      );
    }
    
    console.log('Conexão removida com sucesso');
  };

  // Handle bulk operations
  const handleBulkMap = (mappingRules) => {
    const newConnections = [];
    
    mappingRules?.forEach(rule => {
      const matchingMedia = filteredMedia?.filter(item => 
        rule?.pattern && item?.tags?.some(tag => tag?.includes(rule?.pattern))
      );
      
      matchingMedia?.forEach(media => {
        const targetLessons = mockCourseStructure
          ?.flatMap(course => course?.modules)
          ?.flatMap(module => module?.lessons)
          ?.filter(lesson => lesson?.mediaRequired && !lesson?.currentMedia);
        
        if (targetLessons?.length > 0) {
          newConnections?.push({
            sourceId: media?.id,
            targetId: targetLessons?.[0]?.id,
            connectionId: `bulk-conn-${Date.now()}-${media?.id}`,
            status: 'active'
          });
        }
      });
    });
    
    setConnections(prev => [...prev, ...newConnections]);
    console.log(`${newConnections?.length} conexões criadas via mapeamento em lote`);
  };

  // Handle preview
  const handlePreviewMapping = (sourceId, targetId) => {
    const sourceItem = mockMediaLibrary?.find(item => item?.id === sourceId);
    const targetLesson = mockCourseStructure
      ?.flatMap(course => course?.modules)
      ?.flatMap(module => module?.lessons)
      ?.find(lesson => lesson?.id === targetId);
    
    if (sourceItem && targetLesson) {
      setPreviewModal({
        isOpen: true,
        mapping: { source: sourceItem, target: targetLesson }
      });
    }
  };

  // Handle export configuration
  const handleExportConfiguration = () => {
    const configData = {
      connections,
      mediaLibrary: mockMediaLibrary,
      courseStructure: mockCourseStructure,
      exportedAt: new Date()?.toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'media-mapping-configuration.json';
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('Configuração de mapeamento exportada');
  };

  // Handle import configuration
  const handleImportConfiguration = (file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const configData = JSON.parse(e?.target?.result);
        setConnections(configData?.connections || []);
        console.log('Configuração de mapeamento importada com sucesso');
      } catch (error) {
        console.error('Erro ao importar configuração:', error);
        setValidationErrors(prev => [...prev, 'Arquivo de configuração inválido']);
      }
    };
    reader?.readAsText(file);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <AdminSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-72'
      }`}>
        <div className="p-6">
          <Breadcrumb customItems={breadcrumbItems} />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Mapeamento de Mídia
              </h1>
              <p className="text-muted-foreground font-caption">
                Configure conexões entre conteúdo de curso e fontes de mídia externas
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button 
                variant="outline" 
                iconName="Settings" 
                iconPosition="left"
                onClick={() => setShowConfiguration(!showConfiguration)}
              >
                Configurações
              </Button>
              <Button 
                variant="outline" 
                iconName="Layers" 
                iconPosition="left"
                onClick={() => setShowBulkTools(!showBulkTools)}
              >
                Ferramentas em Lote
              </Button>
              <Button 
                variant="outline" 
                iconName="Download" 
                iconPosition="left"
                onClick={handleExportConfiguration}
              >
                Exportar
              </Button>
              <label>
                <Button 
                  variant="default" 
                  iconName="Upload" 
                  iconPosition="left"
                  asChild
                >
                  <span>Importar</span>
                </Button>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => handleImportConfiguration(e?.target?.files?.[0])}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6 shadow-tier-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Total de Mídia</p>
                  <p className="text-2xl font-bold text-foreground">{mappingStats?.total}</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name="Archive" size={24} color="var(--color-primary)" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 shadow-tier-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Mapeados</p>
                  <p className="text-2xl font-bold text-success">{mappingStats?.mapped}</p>
                </div>
                <div className="p-3 bg-success/10 rounded-lg">
                  <Icon name="CheckCircle" size={24} color="var(--color-success)" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 shadow-tier-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Não Mapeados</p>
                  <p className="text-2xl font-bold text-warning">{mappingStats?.unmapped}</p>
                </div>
                <div className="p-3 bg-warning/10 rounded-lg">
                  <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 shadow-tier-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Conflitos</p>
                  <p className="text-2xl font-bold text-error">{mappingStats?.conflicts}</p>
                </div>
                <div className="p-3 bg-error/10 rounded-lg">
                  <Icon name="AlertCircle" size={24} color="var(--color-error)" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 shadow-tier-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Taxa de Mapeamento</p>
                  <p className="text-2xl font-bold text-foreground">{mappingStats?.mappingRate}%</p>
                </div>
                <div className="p-3 bg-secondary/10 rounded-lg">
                  <Icon name="TrendingUp" size={24} color="var(--color-secondary)" />
                </div>
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors?.length > 0 && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <div className="flex-1">
                  <h4 className="font-medium text-error mb-2">Problemas de Validação</h4>
                  <ul className="space-y-1">
                    {validationErrors?.map((error, index) => (
                      <li key={index} className="text-sm text-error">{error}</li>
                    ))}
                  </ul>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setValidationErrors([])}
                />
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6 shadow-tier-1">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <Input
                  type="search"
                  placeholder="Buscar mídia por título, arquivo ou tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="lg:max-w-md"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <Select
                  value={mediaTypeFilter}
                  onValueChange={setMediaTypeFilter}
                  options={typeFilterOptions}
                  className="w-48"
                />
                
                <Select
                  value={mappingStatusFilter}
                  onValueChange={setMappingStatusFilter}
                  options={statusFilterOptions}
                  className="w-48"
                />
              </div>
            </div>
          </div>

          {/* Bulk Mapping Tools */}
          {showBulkTools && (
            <BulkMappingTools
              onBulkMap={handleBulkMap}
              onClose={() => setShowBulkTools(false)}
              availableMedia={filteredMedia}
              courseStructure={mockCourseStructure}
            />
          )}

          {/* Configuration Panel */}
          {showConfiguration && (
            <ConfigurationPanel
              onClose={() => setShowConfiguration(false)}
              onSave={(config) => {
                console.log('Configurações salvas:', config);
                setShowConfiguration(false);
              }}
            />
          )}

          {/* Main Mapping Interface */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
            {/* Source Media Panel */}
            <SourceMediaPanel
              media={filteredMedia}
              selectedItems={selectedSourceItems}
              onSelectItems={setSelectedSourceItems}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              connections={connections}
              onPreview={(item) => handlePreviewMapping(item?.id, null)}
            />

            {/* Target Course Panel */}
            <TargetCoursePanel
              courseStructure={mockCourseStructure}
              selectedItems={selectedTargetItems}
              onSelectItems={setSelectedTargetItems}
              onDrop={handleDropOnTarget}
              connections={connections}
              onRemoveConnection={handleRemoveConnection}
              onPreview={(lessonId) => {
                const connection = connections?.find(conn => conn?.targetId === lessonId);
                if (connection) {
                  handlePreviewMapping(connection?.sourceId, lessonId);
                }
              }}
            />

            {/* Mapping Connections Overlay */}
            <MappingConnectionsOverlay
              connections={connections}
              mediaLibrary={mockMediaLibrary}
              courseStructure={mockCourseStructure}
              onConnectionClick={(connection) => 
                handlePreviewMapping(connection?.sourceId, connection?.targetId)
              }
            />
          </div>
        </div>
      </main>

      {/* Preview Modal */}
      {previewModal?.isOpen && (
        <PreviewModal
          mapping={previewModal?.mapping}
          onClose={() => setPreviewModal({ isOpen: false, mapping: null })}
          onConfirmMapping={(sourceId, targetId) => {
            const existingConnection = connections?.find(conn => 
              conn?.sourceId === sourceId && conn?.targetId === targetId
            );
            if (!existingConnection) {
              const newConnection = {
                sourceId,
                targetId,
                connectionId: `preview-conn-${Date.now()}`,
                status: 'active'
              };
              setConnections(prev => [...prev, newConnection]);
            }
            setPreviewModal({ isOpen: false, mapping: null });
          }}
        />
      )}

      <FloatingAIChat />
    </div>
  );
};

export default MediaMappingConfiguration;